import { useEffect, useRef, useState } from "react";
import {
  OmsExportJob,
  OmsExportPreset,
  OmsFilterOperator,
  OmsReport,
} from "../lusca-search-api/generated-src";
import { OmsColumnsDraft } from "../components/dashboard/OMSColumns";
import type { OmsConfiguration } from "../components/dashboard/omsPresetEditing";
import { columnValueError } from "../components/dashboard/omsReportFormatting";
import {
  changeCondition,
  conditionOperators,
  OmsFilterCondition,
  prefersOmsChoices,
  submittedCondition,
} from "../components/dashboard/omsFilterEditing";

export type { OmsFilterCondition } from "../components/dashboard/omsFilterEditing";
export type OmsFilterDraft = {
  snapshotId: string;
  report?: OmsReport;
  editing?: boolean;
  criteria: OmsFilterCondition[];
  operation: "and" | "or";
  tab: string;
  columns?: OmsColumnsDraft;
  columnsExpanded?: boolean;
  workspace?: "rows" | "columns";
  appliedPreset?: OmsExportPreset;
  presetEditor?: {
    view: "export" | "edit" | "save" | "manage";
    name: string;
    scope: "filters" | "columns" | "both";
    updating?: boolean;
    returnTo?: "save" | "actions" | "edit";
    previous?: OmsConfiguration;
    previousReport?: OmsReport;
    previousWorkspace?: "rows" | "columns";
    previousRequests?: OmsFilterDraft["requests"];
    cancelTo?: "export" | "manage";
  };
  request?: { mode: "full" | "filtered"; identity: string; id: string };
  requests?: {
    full?: { identity: string; id: string };
    filtered?: { identity: string; id: string };
  };
  undo?: OmsConfiguration;
  presetRequest?: { identity: string; id: string };
};
export default function useOmsFilterCriteria(
  report: OmsReport,
  job?: OmsExportJob,
  draft?: OmsFilterDraft
) {
  const initialDraft =
    draft?.snapshotId === report.snapshotId ? draft : undefined;
  const [criteria, setCriteria] = useState<OmsFilterCondition[]>(
    initialDraft?.criteria || [
      { id: 0, column: "", operator: OmsFilterOperator.Eq, value: "" },
    ]
  );
  const [operation, setOperation] = useState<"and" | "or">(
    initialDraft?.operation || "and"
  );
  const nextId = useRef(Math.max(-1, ...criteria.map((item) => item.id)) + 1);
  const restoredJob = useRef<string | null>(null);
  useEffect(() => {
    if (job?.mode === "filtered" && restoredJob.current !== job.jobId) {
      restoredJob.current = job.jobId;
      setCriteria(job.filters.map((criterion, id) => ({ ...criterion, id })));
      nextId.current = job.filters.length;
      setOperation(job.operation);
    }
  }, [job]);
  function update(id: number, change: Partial<OmsFilterCondition>) {
    setCriteria((previous) =>
      previous.map((criterion) =>
        criterion.id === id
          ? {
              ...criterion,
              ...(change.value !== undefined ? { values: undefined } : {}),
              ...(change.values !== undefined ? { value: undefined } : {}),
              ...change,
              valueNotice: undefined,
            }
          : criterion
      )
    );
  }
  function setOperator(id: number, operator: OmsFilterOperator) {
    setCriteria((previous) =>
      previous.map((criterion) =>
        criterion.id === id ? changeCondition(criterion, operator) : criterion
      )
    );
  }
  function setColumn(id: number, column: string) {
    setCriteria((previous) =>
      previous.map((criterion) => {
        if (criterion.id !== id || criterion.column === column)
          return criterion;
        if (!criterion.column) {
          const choices = prefersOmsChoices(report, column);
          return {
            id,
            column,
            operator: (choices ? "in" : "eq") as OmsFilterOperator,
            value: choices ? undefined : "",
            values: choices ? [] : undefined,
          };
        }
        const changed: OmsFilterCondition = {
          ...criterion,
          column,
          valueNotice: undefined,
        };
        const allowed = conditionOperators(report, changed);
        const next = allowed.includes(criterion.operator)
          ? changed
          : changeCondition(changed, OmsFilterOperator.Eq);
        // Keep exact input across fields; inline validation explains incompatible types.
        if ((report.columnTypes?.[column] || "string") !== "string") {
          next.values = undefined;
          if (criterion.values && criterion.values.length > 1)
            next.valueNotice =
              "Multiple selections cannot be used for this field. Enter a single value.";
        }
        return next;
      })
    );
  }
  function add() {
    const id = nextId.current;
    nextId.current += 1;
    setCriteria((previous) => [
      ...previous,
      { id, column: "", operator: OmsFilterOperator.Eq, value: "" },
    ]);
  }
  function remove(index: number) {
    setCriteria((previous) => previous.filter((_item, i) => i !== index));
  }
  function clear() {
    const id = nextId.current;
    nextId.current += 1;
    setCriteria([
      { id, column: "", operator: OmsFilterOperator.Eq, value: "" },
    ]);
    setOperation("and");
  }
  function replace(filters: OmsFilterCondition[], nextOperation: "and" | "or") {
    setCriteria(filters);
    nextId.current = Math.max(-1, ...filters.map((item) => item.id)) + 1;
    setOperation(nextOperation);
  }
  return {
    criteria,
    operation,
    setOperation,
    update,
    setOperator,
    setColumn,
    add,
    remove,
    clear,
    replace,
    filters: criteria.map(submittedCondition),
    valid:
      criteria.length > 0 &&
      criteria.every(
        (criterion) =>
          criterion.column &&
          report.columns.includes(criterion.column) &&
          conditionOperators(report, criterion).includes(criterion.operator) &&
          !criterion.valueNotice &&
          !columnValueError(
            report.columnTypes?.[criterion.column] || "string",
            criterion.operator,
            criterion.value,
            criterion.values
          )
      ),
  };
}
export type OmsFilterCriteriaState = ReturnType<typeof useOmsFilterCriteria>;
